import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import { getErrorMessage } from '../../utils/validators';
import Icon from '../Icon/Icon';
import ForgotPasswordModal from '../ForgotPasswordModal/ForgotPasswordModal';
import styles from './LoginModal.module.css';

/**
 * LoginModal Component - TECNILAND Login Form
 * Modal para iniciar sesión con username/email y contraseña
 * DESKTOP-FIRST: Optimizado para 1920px+
 */
function LoginModal({ isOpen, onClose, onSwitchToRegister }) {
  const { login } = useAuth();
  const modalRef = useRef(null);
  const usernameInputRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  // Estado de errores
  const [errors, setErrors] = useState({
    username: '',
    password: '',
    general: ''
  });
  
  // Estado de carga
  const [isLoading, setIsLoading] = useState(false);
  
  // Estado del modal de recuperación de contraseña
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Auto-focus en el input de username al abrir el modal
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      
      // Bloquear scroll del body
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Focus al input después de que se renderice
      setTimeout(() => {
        if (usernameInputRef.current) {
          usernameInputRef.current.focus();
        }
      }, 100);
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Restaurar focus al cerrar
  useEffect(() => {
    if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Manejar cambios en inputs
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo al escribir
    setErrors(prev => ({
      ...prev,
      [name]: '',
      general: ''
    }));
  }, []);

  // Validar formulario
  const validateForm = useCallback(() => {
    const newErrors = {
      username: '',
      password: '',
      general: ''
    };
    
    if (!formData.username.trim()) {
      newErrors.username = 'El nombre de usuario es requerido';
    }
    
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Mínimo 6 caracteres';
    }
    
    setErrors(newErrors);
    
    return !newErrors.username && !newErrors.password;
  }, [formData]);

  // Manejar submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));
    
    try {
      const response = await authService.login(formData.username, formData.password);
      
      if (response.success) {
        // Login exitoso
        login(response.data.token, response.data.user);
        
        // Limpiar formulario
        setFormData({ username: '', password: '' });
        setErrors({ username: '', password: '', general: '' });
        
        // Cerrar modal
        onClose();
      } else {
        // Error del backend
        const errorMessage = getErrorMessage(response.code, response.error);
        
        // Mostrar error en el campo correspondiente si es específico
        if (response.code === 'USERNAME_REQUIRED') {
          setErrors(prev => ({ ...prev, username: errorMessage }));
        } else if (response.code === 'PASSWORD_REQUIRED') {
          setErrors(prev => ({ ...prev, password: errorMessage }));
        } else {
          setErrors(prev => ({ ...prev, general: errorMessage }));
        }
      }
    } catch (error) {
      console.error('Error en login:', error);
      setErrors(prev => ({ 
        ...prev, 
        general: 'Error de conexión con el servidor' 
      }));
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, login, onClose]);

  // Cerrar al hacer click fuera del modal
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Cambiar a registro
  const handleSwitchToRegister = useCallback((e) => {
    e.preventDefault();
    setFormData({ username: '', password: '' });
    setErrors({ username: '', password: '', general: '' });
    onSwitchToRegister();
  }, [onSwitchToRegister]);

  // No renderizar si no está abierto
  if (!isOpen) return null;

  return (
    <div 
      className={styles.modalBackdrop} 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-title"
    >
      <div className={styles.modalContent} ref={modalRef}>
        {/* Botón cerrar */}
        <button 
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Cerrar"
          type="button"
        >
          <Icon name="close" size="sm" />
        </button>

        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.iconWrapper}>
            <Icon name="profile" size="lg" animation="pulse" />
          </div>
          <h2 id="login-title" className={styles.title}>Iniciar Sesión</h2>
          <p className={styles.subtitle}>Bienvenido de vuelta a TECNILAND</p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Error general */}
          {errors.general && (
            <div className={styles.errorGeneral}>
              <Icon name="alert" size="sm" />
              <span>{errors.general}</span>
            </div>
          )}

          {/* Campo Username */}
          <div className={styles.inputGroup}>
            <label htmlFor="login-username" className={styles.label}>
              Nombre de usuario
            </label>
            <div className={`${styles.inputWrapper} ${errors.username ? styles.inputError : ''}`}>
              <Icon name="profile" size="sm" />
              <input
                ref={usernameInputRef}
                type="text"
                id="login-username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Tu nombre de usuario"
                autoComplete="username"
                disabled={isLoading}
                className={styles.input}
              />
            </div>
            {errors.username && (
              <span className={styles.errorText}>{errors.username}</span>
            )}
          </div>

          {/* Campo Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="login-password" className={styles.label}>
              Contraseña
            </label>
            <div className={`${styles.inputWrapper} ${errors.password ? styles.inputError : ''}`}>
              <Icon name="shield" size="sm" />
              <input
                type="password"
                id="login-password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Tu contraseña"
                autoComplete="current-password"
                disabled={isLoading}
                className={styles.input}
              />
            </div>
            {errors.password && (
              <span className={styles.errorText}>{errors.password}</span>
            )}
          </div>

          {/* Link olvidé contraseña */}
          <button
            type="button"
            className={styles.forgotPassword}
            onClick={() => setShowForgotPassword(true)}
          >
            ¿Olvidaste tu contraseña?
          </button>

          {/* Botón submit */}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className={styles.loadingSpinner}></span>
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <Icon name="login" size="sm" />
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>
        </form>

        {/* Footer - Cambiar a registro */}
        <div className={styles.modalFooter}>
          <p>¿No tienes cuenta?</p>
          <button 
            type="button"
            className={styles.switchLink}
            onClick={handleSwitchToRegister}
          >
            Regístrate aquí
          </button>
        </div>

        {/* Decoración de fondo */}
        <div className={styles.modalDecoration}></div>
      </div>

      {/* Modal de recuperación de contraseña */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onBackToLogin={() => setShowForgotPassword(false)}
      />
    </div>
  );
}

export default LoginModal;
